import { createClientAccount } from "api/client";
import Button from "components/Button";
import ReusableForm from "components/Form";
import Modal from "components/Modal";
import Notification, { NotificationProps } from "components/Notification";
import { FieldType, FormData } from "components/types";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { handleResponse } from "components/utility";
import { useQueryClient } from "react-query";
import { Option } from "components/Select";
import { GENDER_OPTIONS, TITLE_OPTIONS, validateAustralianPhoneNumber } from "utils/util";

interface CreateClientAccountProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const validateAgedCareReferralCode = (value: string) => {
  const codeLength = value.length; 
  if (codeLength >= 12 && codeLength <= 14) {
    const valueToUpper = value.toUpperCase();
    const prefix = valueToUpper.substring(0, 2);
    if (["R-", "A-", "S-"].includes(prefix)) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

const getFields = (
  username: string,
  titleOptions: Option[],
  onChangeTitle: (title: {label: String, value: String}) => void
) => [
  {
    name: "gender",
    label: "Gender",
    type: FieldType.Select,
    placeholder: "Select gender",
    options: GENDER_OPTIONS,
    requiredCondition: "This is a required field",
    hideSearch: true,
    className: "w-full",
    parentFormContainerClassName:
      "md:w-1/2 w-full  inline-block align-top md:pr-5",
    containerClassName: "w-full",
  },
  {
    name: "title",
    label: "Title",
    type: FieldType.Select,
    placeholder: "Select title",
    options: titleOptions,
    requiredCondition: "This is a required field",
    hideSearch: true,
    className: "w-full",
    parentFormContainerClassName: "md:w-1/2 w-full  inline-block align-top",
    containerClassName: "w-full",
    onChange: onChangeTitle,
  },
  {
    name: "firstName",
    label: "First Name",
    type: FieldType.Input,
    placeholder: "John",
    requiredCondition: "This is a required field",
    className: "w-full",
    parentFormContainerClassName:
      "md:w-1/3 w-full  inline-block align-top md:pr-5",
    containerClassName: "w-full",
  },
  {
    name: "lastName",
    label: "Last Name",
    type: FieldType.Input,
    placeholder: "Doe",
    requiredCondition: "This is a required field",
    className: "w-full",
    parentFormContainerClassName: "md:w-1/3 w-full  inline-block align-top md:pr-5",
    containerClassName: "w-full",
  },
  {
    name: "username",
    label: "Username",
    type: FieldType.Input,
    placeholder: "JDoe",
    requiredCondition: "This is a required field",
    defaultValue: username,
    isDisabled: true,
    className: "w-full",
    parentFormContainerClassName: "md:w-1/3 w-full  inline-block align-top",
    containerClassName: "w-full",
  },
  {
    name: "mobileNumber",
    label: "Mobile number",
    type: FieldType.Input,
    placeholder: "04XXX XXX XXX",
    requiredCondition: "This is a required field",
    validationFunction: validateAustralianPhoneNumber,
    validationErrorMessage: "Invalid Australian phone number",
  },
  {
    name: "email",
    label: "Email",
    type: FieldType.EmailInput,
    placeholder: "jdoe@gmail.com",
    requiredCondition: "This is a required field",
  },
  {
    name: "agedCareReferralCode",
    label: "Aged Care Referral Code",
    type: FieldType.Input,
    placeholder: "R-1234567890AB",
    requiredCondition: "This is a required field",
    validationFunction: validateAgedCareReferralCode,
    validationErrorMessage: "Invalid Aged Care Referral Code format",
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === " ") {
        e.preventDefault();
      }
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value.toUpperCase();
    },
  },
  {
    name: "assignedHcpLevel",
    label: "Assigned HCP level",
    type: FieldType.Select,
    placeholder: "Select",
    className: "w-full ",
    requiredCondition: "This is a required field",
    options: [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
      { label: "6", value: 6 },
      { label: "7", value: 7 },
      { label: "8", value: 8 },
    ],
    parentFormContainerClassName: "w-full",
    containerClassName: "w-full ",
    hideSearch: true,
  },
  {
    name: "role",
    label: "User Role",
    type: FieldType.Input,
    placeholder: "Normal",
    defaultValue: "normal",
    isDisabled: true,
  },
];

const CreateClientAccount: React.FC<CreateClientAccountProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [response, setResponse] = useState<NotificationProps | null>(null);
  const queryClient = useQueryClient();
  const handleCloseModal = () => {
    setResponse(null);
    setIsOpen(false);
    reset();
  };

  const onSubmit = async (data: FormData) => {
    setResponse(null);
    const response = await createClientAccount(data);
    const isSuccess = await handleResponse(response, setError, setResponse);
    if (isSuccess) {
      setResponse({
        type: "success",
        message: "Client account sucessfuly created!",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/user/admin/clients`] });
    }
  };

  const { reset, formState, setError, watch, setValue, ...formMethods } = useForm<FormData>({ mode: "onChange" });
  const selectedGender = watch("gender");
  const selectedTitle = watch("title");

  const { isValid } = formState;
  const [username, setUsername] = useState("");
  const watchedUsername = watch(["firstName", "lastName"]);

  useEffect(() => {
    if (
      watchedUsername.length >= 2 &&
      watchedUsername[0] &&
      watchedUsername[1]
    ) {
      setUsername(watchedUsername[0][0] + watchedUsername[1]);
    }
  }, [watchedUsername]);

  /* ---------- FILTER TITLE BASED ON GENDER ---------- */
  const filteredTitleOptions = React.useMemo(() => {
    if (selectedGender === "Male") {
      return TITLE_OPTIONS.filter(o =>
        [ "Dr", "Mr"].includes(o.value)
      );
    }

    if (selectedGender === "Female") {
      return TITLE_OPTIONS.filter(o =>
        ["Dr", "Mrs", "Miss"].includes(o.value)
      );
    }

    return TITLE_OPTIONS;
  }, [selectedGender]);

  /* ---------- RESET INVALID VALUES ---------- */
  useEffect(() => {
    if (
      selectedTitle &&
      !filteredTitleOptions.some(o => o.value === selectedTitle)
    ) {
      setValue("title", "");
    }
  }, [filteredTitleOptions, selectedTitle, setValue]);

  const onChangeTitle = (title: {label: String, value: String}) => {
    if (title.value === "Mr") {
      setValue("gender", "Male");
    } else if (title.value === "Mrs" || title.value === "Miss") {
      setValue("gender", "Female");
    }
  }

  return (
    <div>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        modalHeader="Create client account"
        customModalStyles={{ maxWidth: "574px", width: "574px" }}
      >
        {response && (
          <Notification type={response.type} message={response.message} />
        )}
        <ReusableForm
          fields={getFields(username, filteredTitleOptions, onChangeTitle)}
          onSubmit={onSubmit}
          id="hook-form"
          formClassName="m-4 px-3"
          formMethods={{
            ...formMethods,
            reset,
            formState,
            setError,
            watch,
            setValue
          }}
        />
        <div className="modal-footer bg-gray-50 flex justify-end items-center  rounded-b-md px-6 py-2.5 border-t border-gray-200  rounded-b-md mt-6">
          <Button variant="white" onClick={handleCloseModal} className="mr-3">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="hook-form"
            disabled={!isValid}
          >
            Create account
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CreateClientAccount;
